;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();export function base64ToBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: contentType });
}

export const PREVIEWABLE_IMAGE_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'heic',
  'heif',
] as const;

function extensionFromPath(value: string): string {
  const clean = value.split('?')[0]?.split('#')[0] ?? '';
  const base = clean.split('/').pop() ?? clean;
  const idx = base.lastIndexOf('.');
  return idx >= 0 ? base.slice(idx + 1).toLowerCase() : '';
}

function isKnownPreviewExtension(ext: string): boolean {
  return (
    ext === 'pdf' ||
    ext === 'xlsx' ||
    ext === 'xls' ||
    ext === 'csv' ||
    (PREVIEWABLE_IMAGE_EXTENSIONS as readonly string[]).includes(ext)
  );
}

function extensionFromUrl(fileUrl: string): string {
  try {
    return extensionFromPath(new URL(fileUrl).pathname);
  } catch {
    return extensionFromPath(fileUrl);
  }
}

export function getPreviewFileExtension(name: string, fileUrl?: string): string {
  const fromName = extensionFromPath(name);
  if (isKnownPreviewExtension(fromName)) return fromName;
  if (!fileUrl) return fromName;
  const fromUrl = extensionFromUrl(fileUrl);
  if (isKnownPreviewExtension(fromUrl)) return fromUrl;
  return fromName || fromUrl;
}

export function getPreviewContentTypeFromExtension(name: string, fileUrl?: string): string | null {
  const ext = getPreviewFileExtension(name, fileUrl);
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'bmp') return 'image/bmp';
  return null;
}

export function getPreviewContentTypeFromBytes(bytes: Uint8Array): string | null {
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46
  )
    return 'application/pdf';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff)
    return 'image/jpeg';
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  )
    return 'image/png';
  if (bytes.length >= 3 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46)
    return 'image/gif';
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  )
    return 'image/webp';
  return null;
}

export function isSpreadsheetFileName(name: string, fileUrl?: string): boolean {
  const ext = getPreviewFileExtension(name, fileUrl);
  return ext === 'xlsx' || ext === 'xls' || ext === 'csv';
}

function containsAscii(bytes: Uint8Array, needle: string): boolean {
  const needleLength = needle.length;
  if (needleLength === 0 || bytes.length < needleLength) return false;

  outer: for (let i = 0; i <= bytes.length - needleLength; i++) {
    for (let j = 0; j < needleLength; j++) {
      if (bytes[i + j] !== needle.charCodeAt(j)) continue outer;
    }
    return true;
  }
  return false;
}

/** ZIP xlsx (xl/) or OLE xls, for attachments whose URL has no file extension. */
export function looksLikeSpreadsheetBuffer(bytes: Uint8Array): boolean {
  if (
    bytes.length >= 8 &&
    bytes[0] === 0xd0 &&
    bytes[1] === 0xcf &&
    bytes[2] === 0x11 &&
    bytes[3] === 0xe0
  )
    return true;

  if (bytes.length < 4 || bytes[0] !== 0x50 || bytes[1] !== 0x4b) return false;

  const tailStart = Math.max(0, bytes.length - 65536);
  const sample = bytes.subarray(tailStart);
  return containsAscii(sample, 'xl/workbook') || containsAscii(sample, 'xl/worksheets');
}

const SAFE_INLINE_PREVIEW_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
] as const;

export function isSafeInlinePreviewContentType(contentType: string): boolean {
  const base = contentType.split(';')[0]?.trim().toLowerCase() ?? '';
  return (SAFE_INLINE_PREVIEW_CONTENT_TYPES as readonly string[]).includes(base);
}

export function isPdfFileName(name: string, fileUrl?: string): boolean {
  return getPreviewFileExtension(name, fileUrl) === 'pdf';
}

export function isPreviewableImageFileName(name: string, fileUrl?: string): boolean {
  return (PREVIEWABLE_IMAGE_EXTENSIONS as readonly string[]).includes(
    getPreviewFileExtension(name, fileUrl)
  );
}

export function isBlobUrl(url: string): boolean {
  return url.startsWith('blob:');
}

/** Remote PDFs need a proxied blob so the browser can preview instead of download. */
export function needsProxiedPdfPreview(name: string, fileUrl: string): boolean {
  return isPdfFileName(name, fileUrl) && !isBlobUrl(fileUrl);
}

export type FilePreviewKind = 'image' | 'pdf' | 'other';

export function getPreviewKindFromMimeAndName(
  contentType: string,
  name: string,
  fileUrl?: string
): FilePreviewKind {
  const type = contentType.toLowerCase();
  if (type.includes('pdf')) return 'pdf';
  if (type.startsWith('image/')) return 'image';
  if (isPdfFileName(name, fileUrl)) return 'pdf';
  if (isPreviewableImageFileName(name, fileUrl)) return 'image';
  return 'other';
}

export async function getPreviewKind(
  blob: Blob,
  name: string,
  fileUrl?: string
): Promise<FilePreviewKind> {
  const fromMimeAndName = getPreviewKindFromMimeAndName(blob.type, name, fileUrl);
  if (fromMimeAndName !== 'other') return fromMimeAndName;

  const bytes = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46
  )
    return 'pdf';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff)
    return 'image';
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  )
    return 'image';
  if (bytes.length >= 3 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46)
    return 'image';
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  )
    return 'image';

  return 'other';
}
